/**
 * A v4 UUID that works where the tablets actually run.
 *
 * `crypto.randomUUID` is exposed ONLY in a secure context — HTTPS or
 * localhost. The tablets load the app over plain http on the LAN, where it is
 * simply not a function, and the throw landed at the worst possible moment:
 * the instant an operator confirmed work, inside enqueue, before anything was
 * committed. `crypto.getRandomValues` carries no such restriction, so it is
 * the fallback rather than a weaker generator.
 *
 * This id IS the external key the server upserts on, so collision resistance
 * is not cosmetic — Math.random is a last resort for an environment that has
 * no Web Crypto at all, and would be a real (if remote) risk if it were ever
 * reached.
 */
export function newUuid(): string {
  const webCrypto = globalThis.crypto as Crypto | undefined

  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }

  // Stamp version 4 and the RFC 4122 variant, so what comes out is a real
  // UUID and not just random hex that looks like one.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80

  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}`
    + `-${hex.slice(16, 20)}-${hex.slice(20)}`
}
